import React, { useState, useContext, useEffect } from "react";
import { LegacyCard as Card, FormLayout, PageActions, Banner, BlockStack } from "@shopify/polaris";
import PropTypes from "prop-types";
import { useMutation } from "@apollo/client";
import fileDownload from "js-file-download";
import Papa from "papaparse";

// import hoc
import { withFeature } from "lib/hoc";

import constant from "lib/constant/constant";
import { PrivateContext } from "lib/context/privateContext";

import { baseHelper, errorHelper } from "lib/helpers";

// import component
import FileUpload from "lib/components/fileInput/fileUpload";
import { Sheet } from "lib/components";

import csvData from "app/productOld/modules/operator/features/add/addProductConfig";

// import query
import { BULK_UPLOAD } from "app/productOld/apollo/mutations";
import { useNavigate } from "react-router-dom";

const BulkCreate = (props) => {
  const { setBanner } = props;
  const { cms } = useContext(PrivateContext) || {};
  const navigate = useNavigate();
  const { gql } = constant;
  const [values, setValues] = useState({});
  const [isSubmitDisabled, setIsSubmitDisabled] = useState(true);
  const [bulkProductUploadRequest, { loading: bulkUploadLoading }] = useMutation(BULK_UPLOAD);
  const [sheetActive, setSheetActive] = useState(false);
  const [sheetTitle, setSheetTitle] = useState("");
  const [sheetContent, setSheetContent] = useState("");

  const getCSVData = (csvValue) => {
    const regexForEnterKey = /\n,|\n/gi;
    const csvDataAfterRegex = csvValue.data.replace(regexForEnterKey, "\n");
    return csvDataAfterRegex;
  };

  const onSubmit = async () => {
    if (!values.data) {
      setBanner({
        isOpen: true,
        title: cms("common.message.error.csvIsRequired"),
        status: "critical",
      });
      return;
    }
    const modifiedFormat = getCSVData(values);
    const requestData = {
      file: {
        file: modifiedFormat,
        fileName: values.fileName,
      },
    };
    try {
      const res = await bulkProductUploadRequest({
        variables: {
          input: requestData,
        },
      });

      const resData = baseHelper.getResponseData(res.data, gql.BULK_PRODUCT_CSV);
      let banner = {
        isOpen: true,
        status: "info",
        title: cms("section.bulk.message.success.title"),
        children: cms("section.bulk.message.success.content"),
        action: {
          content: cms("section.bulk.message.success.action"),
          onAction: () => navigate("/activity/bulk"),
        },
      };

      if (!resData) {
        const error = baseHelper.getResponseError(res.data, gql.BULK_PRODUCT_CSV);
        banner = {
          action: null,
          isOpen: true,
          status: "critical",
          title: error,
        };
      }
      setIsSubmitDisabled(true);
      setBanner(banner);
    } catch (exception) {
      setBanner({
        isOpen: true,
        status: "critical",
        title: errorHelper.parse(exception),
      });
    }
  };

  const primaryAction = {
    content: cms("common.label.done"),
    onAction: () => setSheetActive(false),
  };

  const secondaryAction = {
    content: cms("common.button.cancel"),
    onAction: () => setSheetActive(false),
  };
  const downloadCSV = () => {
    const csv = Papa.unparse(csvData);
    fileDownload(csv, constant.BULK_PRODUCT_CSV);
  };
  const learnMore = (title, content) => {
    setSheetActive(true);
    setSheetTitle(title);
    setSheetContent(content);
  };

  useEffect(() => {
    if (values.data) {
      setIsSubmitDisabled(false);
    }
    if (!values.data) {
      setIsSubmitDisabled(true);
    }
  }, [values]);

  return (
    <>
      <Card
        title={cms("section.bulk.title")}
        actions={[
          {
            content: cms("common.label.learnMore"),
            onAction: () => learnMore(cms("section.bulk.title"), "TODO:"),
          },
        ]}
      >
        <Card.Section>
          <BlockStack gap="4">
            {cms("section.bulk.description")}
            <br />
            <Banner status="info">
              <p>{cms("section.bulk.label.sampleCsv")}</p>
            </Banner>
          </BlockStack>
          <br />
          <FormLayout>
            <FileUpload setValue={setValues} getCSV={downloadCSV} values={values} />
          </FormLayout>
          <PageActions
            primaryAction={{
              content: cms("section.bulk.button.primary"),
              id: "submitButton",
              onAction: () => onSubmit(),
              loading: bulkUploadLoading,
              disabled: bulkUploadLoading || isSubmitDisabled,
            }}
            secondaryActions={[
              {
                content: cms("common.button.cancel"),
                id: "cancelButton",
                onAction: () => navigate("/"),
              },
            ]}
          />
        </Card.Section>
      </Card>
      <Sheet
        title={sheetTitle}
        isOpen={sheetActive}
        onClose={() => setSheetActive(false)}
        primaryAction={primaryAction}
        secondaryAction={secondaryAction}
      >
        {sheetContent}
      </Sheet>
    </>
  );
};

BulkCreate.propTypes = {
  setBanner: PropTypes.func.isRequired,
};

export default withFeature(BulkCreate, { feature: constant.PRODUCT_BULK_CREATE });
